import { Typography } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import Content from '../components/Layout/Content';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import { Box } from '@mui/system';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import SearchBar from '../components/SearchBar/Search';
import NotFound from '../components/NotFound/NotFoundPage';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';
import {
  immunizationsArrayConvertor,
  extractValueFromCoding,
  getIconFromSubItemsArray,
  calculateDateFilterTimeStamp,
  getStatusFilterParamList,
} from '../shared/helper';
import {
  immunizationsStatusFilterDatas,
  DEFAULT_NULL_REPLACEMENT,
  dateFilterMenuItems,
  NUMBER_OF_ROWS_PER_PAGE,
  IMMUNIZATION_STATUS_LIST,
} from '../shared/constants';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { useSnackbar } from 'notistack';
import { useLazyQuery } from '@apollo/client';
import {
  FETCH_ALL_IMMUNIZATIONS_QUERY,
  FETCH_IMMUNIZATION_DETAILS_BY_ID_QUERY,
} from '../services/ImmunizationsQueries/ImmunizationsService';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import ApplicationContext from '../context/applicationContext';
import {
  IMMUNIZATIONS,
  IMMUNIZATIONS_TABLE_HEADING as tableHeading,
  IMMUNIZATIONS_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

export const Immunizations = () => {
  const [openFlyout, setOpenFlyout] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailsData, setDetailsData] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [totalCount, setTotalCount] = useState();
  const [offset, setOffset] = useState(0);
  const { menuItems } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();

  const [fetchAllImmunizations, fetchAllImmunizationsCall] = useLazyQuery(
    FETCH_ALL_IMMUNIZATIONS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(IMMUNIZATIONS.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        let result = res.fetchAllImmunizations.immunizationList;
        let convetor = JSON.parse(result);
        let data = immunizationsArrayConvertor(convetor);
        setRecordedData(data);
        setFilteredData(data);
        setTotalCount(res.fetchAllImmunizations.count);
      },
    }
  );

  const [fetchImmunizationById, fetchImmunizationByIdCall] = useLazyQuery(
    FETCH_IMMUNIZATION_DETAILS_BY_ID_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(IMMUNIZATIONS.ON_ERROR_TEXT.BY_ID, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        showFlyout(res.fetchImmunizationById);
      },
    }
  );

  const handleChangeFilter = () => {
    setOffset(0);
  };

  useEffect(() => {
    fetchAllImmunizations({
      variables: {
        sourceUrl: '',
        count: NUMBER_OF_ROWS_PER_PAGE.toString(),
        offset: offset,
        dateFilter: calculateDateFilterTimeStamp(timeFilter) || 0,
        statusFilter: getStatusFilterParamList(
          statusFilter,
          immunizationsStatusFilterDatas,
          IMMUNIZATION_STATUS_LIST
        ),
        searchFilter: searchValue,
      },
    });
  }, [offset, searchValue, timeFilter, statusFilter]);

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const statusFilterOptions = {
    options: immunizationsStatusFilterDatas,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const showFlyout = result => {
    const parseResult = JSON.parse(result?.immunization);
    const tableRowInfo = recordsData.find(obj => obj.id === selectedTableId);
    const modalData = [
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.STATUS,
        value: tableRowInfo.status,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.RECORDED_ON,
        value: tableRowInfo.recordedOn,
        width: 6,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.RECORDED_BY,
        value:
          parseResult?.performer?.[0]?.actor?.display ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.BODY_SITE,
        value:
          extractValueFromCoding(parseResult?.site) || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.ROUTE,
        value:
          extractValueFromCoding(parseResult?.route) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.DOSES,
        value:
          parseResult?.doseQuantity && parseResult?.doseQuantity.value
            ? `${parseResult?.doseQuantity.value}${
                parseResult?.doseQuantity.unit
                  ? parseResult?.doseQuantity.unit
                  : ''
              }`
            : DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.NDC_CODE,
        value:
          parseResult?.vaccineCode.coding?.[0].code || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.VACCINE_LOT_NO,
        value: parseResult?.lotNumber || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.VACCINE_MANUFACTURER,
        value:
          parseResult?.manufacturer && parseResult?.manufacturer.display
            ? parseResult?.manufacturer.display
            : DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.VACCINE_DOSE_NO,
        value:
          parseResult?.protocolApplied?.[0].doseNumberPositiveInt ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.REASON,
        value:
          extractValueFromCoding(parseResult?.reasonCode?.[0]) ||
          DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.REACTION,
        value:
          parseResult?.reaction?.[0].detail.display || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.FACILITY,
        value: parseResult?.encounter?.display || DEFAULT_NULL_REPLACEMENT,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.NOTE,
        value: parseResult?.note?.[0].text || DEFAULT_NULL_REPLACEMENT,
        width: 12,
      },
      {
        column_name: IMMUNIZATIONS.MODAL_DATA.DOCUMENTS,
        value: result?.docArn?.[0] || DEFAULT_NULL_REPLACEMENT,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = id => {
    setSelectedTableId(id);
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.immunization);
    fetchImmunizationById({
      variables: {
        immunizationId: id,
      },
    });
  };

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{IMMUNIZATIONS.TITLE_TEXT}</Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={IMMUNIZATIONS.SEARCH_PLACEHOLDER_TEXT}
          setOffset={setOffset}
        />
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...statusFilterOptions} filterType="Status:" />
      </Box>

      <Box sx={table}>
        {!fetchAllImmunizationsCall.loading && !filteredData.length ? (
          <NotFound title={IMMUNIZATIONS.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Medical?.subItems,
                'Immunizations'
              )
            : null
        }
        category={IMMUNIZATIONS.TITLE_TEXT}
        title={modalTitle}></ServiceFlyout>
      <SimpleBackdrop
        open={
          fetchAllImmunizationsCall.loading || fetchImmunizationByIdCall.loading
        }
      />
    </Content>
  );
};
